import { type FunctionReference, anyApi } from "convex/server";
import { type GenericId as Id } from "convex/values";

export const api: PublicApiType = anyApi as unknown as PublicApiType;
export const internal: InternalApiType = anyApi as unknown as InternalApiType;

export type PublicApiType = {
  appCategories: {
    add: {
      category: FunctionReference<"mutation", "public", { name: string; tags: Array<string> }, any>;
    };
    delete: {
      category: FunctionReference<"mutation", "public", { category: Id<"appCategories"> }, any>;
    };
    list: {
      categories: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{ name: string; tags: Array<string> }>
      >;
    };
    update: {
      category: FunctionReference<"mutation", "public", { name: string; tags: Array<string> }, any>;
    };
  };
  apps: {
    add: {
      app: FunctionReference<
        "mutation",
        "public",
        {
          bundleId: string;
          category: string;
          iconUrl: string;
          name: string;
          tags: Array<string>;
          trackId: string;
          version?: string;
        },
        any
      >;
    };
    delete: {
      app: FunctionReference<"mutation", "public", { app: Id<"apps"> }, any>;
    };
    get: {
      apps: FunctionReference<
        "query",
        "public",
        { apps: Array<Id<"apps">> },
        Array<{
          bundleId: string;
          iconUrl: string;
          name: string;
          webkit?: Array<string>;
        }>
      >;
    };
    list: {
      all: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          query?: string;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            bundleId: string;
            category: string;
            iconUrl: string;
            id: Id<"apps">;
            name: string;
            system: boolean;
            tags: Array<string>;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      inLevelFilter: FunctionReference<
        "query",
        "public",
        {
          level: Id<"levels">;
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          query?: string;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            bundleId: string;
            category: string;
            iconUrl: string;
            name: string;
            system: boolean;
            tags: Array<string>;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      inLevelStatic: FunctionReference<
        "query",
        "public",
        { level: Id<"levels"> },
        Array<{
          bundleId: string;
          category: string;
          iconUrl: string;
          name: string;
          system: boolean;
          tags: Array<string>;
          trackId: string;
        }>
      >;
      allowedByDeviceFilter: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          query?: string;
          serial: string;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            bundleId: string;
            category: string;
            iconUrl: string;
            id: Id<"apps">;
            name: string;
            tags: Array<string>;
            trackId: string;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      allowedByDeviceStatic: FunctionReference<
        "query",
        "public",
        { deviceCode?: string; serial?: string },
        Array<{
          bundleId: string;
          category: string;
          iconUrl: string;
          id: Id<"apps">;
          name: string;
          system: boolean;
          tags: Array<string>;
          trackId: string;
        }>
      >;
      installedOnDevice: FunctionReference<
        "query",
        "public",
        { device: Id<"devices"> },
        Array<{
          bundleId: string;
          iconUrl: string;
          name: string;
          trackId: string;
        }>
      >;
      updates: FunctionReference<
        "action",
        "public",
        { serial: string },
        null | Array<{ trackId: string; version: string }>
      >;
      allOnDeviceWithStatus: FunctionReference<
        "action",
        "public",
        { serial: string },
        null | Array<{
          bundleId: string;
          iconUrl?: string;
          name?: string;
          status: "allowed" | "blocked";
          trackId?: string;
          version?: string;
        }>
      >;
    };
    update: {
      tags: FunctionReference<"mutation", "public", { app: Id<"apps">; tags: Array<string> }, any>;
      category: FunctionReference<"mutation", "public", { app: Id<"apps">; category: string }, any>;
      webkit: FunctionReference<"mutation", "public", { app: Id<"apps">; webkit?: Array<string> }, any>;
      system: FunctionReference<"mutation", "public", { app: Id<"apps">; system: boolean }, any>;
      version: FunctionReference<"mutation", "public", { app: Id<"apps">; version?: string }, any>;
      verified: FunctionReference<"mutation", "public", { app: Id<"apps">; verified?: boolean }, null>;
    };
  };
  devices: {
    ads: {
      update: FunctionReference<"mutation", "public", { block: boolean; device: Id<"devices"> }, any>;
    };
    apps: {
      update: FunctionReference<
        "mutation",
        "public",
        {
          allowedApps?: Array<Id<"apps">>;
          appFilter?:
            | Record<
                string,
                | boolean
                | {
                    exclude?: { tags: Array<string>; type: "any" | "all" };
                    include?: { tags: Array<string>; type: "any" | "all" };
                  }
              >
            | true
            | false;
          appMode?: "whitelist" | "blacklist" | "categories";
          blockedApps?: Array<Id<"apps">>;
          device: Id<"devices">;
        },
        any
      >;
      getConfig: FunctionReference<
        "query",
        "public",
        { deviceConfig: Id<"deviceConfigs"> },
        {
          allowedApps: Record<Id<"apps">, null | "device" | "level" | false>;
          appFilter:
            | true
            | false
            | Record<
                string,
                {
                  source: "device" | "level";
                  tagFilter:
                    | boolean
                    | {
                        exclude?: { tags: Array<string>; type: "any" | "all" };
                        include?: { tags: Array<string>; type: "any" | "all" };
                      };
                }
              >;
          appMode: "whitelist" | "blacklist" | "categories";
          blockedApps: Record<Id<"apps">, "device" | "level" | false>;
        }
      >;
      install: FunctionReference<"mutation", "public", { app: Id<"apps">; serial: string }, any>;
      remove: FunctionReference<"mutation", "public", { app: Id<"apps">; serial: string }, any>;
    };
    delete: {
      device: FunctionReference<"mutation", "public", { device: Id<"devices"> }, any>;
      deleteDevice: FunctionReference<"action", "public", { device: Id<"devices"> }, any>;
    };
    get: {
      details: FunctionReference<
        "query",
        "public",
        { device: Id<"devices"> },
        {
          adBlock: boolean;
          device: {
            code: string;
            deviceConfig: Id<"deviceConfigs">;
            jssid?: number;
            profile?: string;
            serial?: string;
          };
          level?: string;
          user?: {
            email?: string;
            firstName: string;
            lastName: string;
            phone?: string;
          };
        }
      >;
      history: FunctionReference<
        "action",
        "public",
        { device: Id<"devices"> },
        {
          completed: Array<{ date_time_completed_epoch: number; name: string }>;
          failed: Array<{
            date_time_failed_epoch: number;
            date_time_issued_epoch: number;
            error: string;
            name: string;
          }>;
          pending: Array<{
            date_time_failed_epoch: number;
            date_time_issued_epoch: number;
            name: string;
            status: string;
          }>;
        }
      >;
    };
    jamf: {
      update: FunctionReference<"action", "public", { device: Id<"devices">; serial: string }, any>;
    };
    level: {
      update: FunctionReference<"mutation", "public", { device: Id<"devices">; level: Id<"levels"> }, any>;
    };
    list: {
      all: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          query?: string;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            deviceConfig: Id<"deviceConfigs">;
            id: Id<"devices">;
            jssid?: number;
            serial?: string;
            user?: {
              email?: string;
              firstName: string;
              lastName: string;
              phone?: string;
            };
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      inGroup: FunctionReference<
        "query",
        "public",
        {
          group: Id<"groups">;
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          query?: string;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            deviceConfig: Id<"deviceConfigs">;
            id: Id<"devices">;
            jssid?: number;
            serial?: string;
            user?: {
              email?: string;
              firstName: string;
              lastName: string;
              phone?: string;
            };
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      inLevel: FunctionReference<
        "query",
        "public",
        {
          level: Id<"levels">;
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            deviceConfig: Id<"deviceConfigs">;
            id: Id<"devices">;
            jssid?: number;
            serial?: string;
            user?: {
              email?: string;
              firstName: string;
              lastName: string;
              phone?: string;
            };
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      withRule: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          rule: Id<"rules">;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{ jssid?: number; serial?: string }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
    };
    policies: {
      update: FunctionReference<
        "mutation",
        "public",
        {
          allowedUrls?: Array<string>;
          blockedUrls?: Array<string>;
          blueJayRules?: Record<string, any> | false;
          browserMode?: "none" | "categories" | "whitelist";
          device: Id<"devices">;
          policyOverrides?: Record<string, boolean> | false;
        },
        any
      >;
      get: FunctionReference<
        "query",
        "public",
        { deviceConfig: Id<"deviceConfigs"> },
        {
          allowlist: Record<string, "device" | "level" | false | null>;
          blocklist: Record<string, "device" | "level" | false>;
          blueJayRules?: Record<string, any> | false;
          browserMode: "none" | "categories" | "whitelist";
          categories: Record<string, "device" | "level" | false>;
        }
      >;
    };
    registrations: {
      create: FunctionReference<
        "mutation",
        "public",
        {
          adBlock: boolean;
          allowedUrls?: Array<string>;
          appFilter?:
            | Record<
                string,
                | boolean
                | {
                    exclude?: { tags: Array<string>; type: "any" | "all" };
                    include?: { tags: Array<string>; type: "any" | "all" };
                  }
              >
            | true
            | false;
          appMode?: "whitelist" | "blacklist" | "categories";
          blockedUrls?: Array<string>;
          blueJayRules?: Record<string, any> | false;
          browserMode?: "none" | "categories" | "whitelist";
          email?: string;
          group?: Id<"groups">;
          level?: Id<"levels">;
          policyOverrides?: Record<string, boolean> | false;
          restrictions?: Record<string, boolean>;
          rules?: Record<Id<"rules">, boolean>;
          user: Id<"users">;
        },
        string
      >;
      all: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            id: Id<"devices">;
            jssid?: number;
            serial?: string;
            user?: {
              email?: string;
              firstName: string;
              lastName: string;
              phone?: string;
            };
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      setSerial: FunctionReference<
        "mutation",
        "public",
        {
          apps?: { allow: Array<string>; block: Array<string> };
          code: string;
          serial: string;
        },
        string
      >;
    };
    restrictions: {
      get: FunctionReference<
        "query",
        "public",
        { deviceConfig: Id<"deviceConfigs"> },
        Record<string, "device" | "level" | false>
      >;
      update: FunctionReference<
        "mutation",
        "public",
        { device: Id<"devices">; restrictions: Record<string, boolean> },
        null
      >;
    };
    rules: {
      get: FunctionReference<
        "query",
        "public",
        { deviceConfig: Id<"deviceConfigs"> },
        Array<{
          bundleId: string;
          description: string;
          name: string;
          parent?: Id<"apps">;
          source: "device" | "level" | false;
        }>
      >;
      update: FunctionReference<
        "mutation",
        "public",
        { device: Id<"devices">; rules: Record<Id<"rules">, boolean> },
        any
      >;
    };
    troubleshoot: {
      get: FunctionReference<
        "query",
        "public",
        { device: Id<"devices"> },
        {
          active: boolean;
          allowAppStore?: boolean;
          allowSafari?: boolean;
          filterBrowsers?: boolean;
          filterSockets?: boolean;
        }
      >;
      update: FunctionReference<
        "mutation",
        "public",
        {
          allowAppStore?: boolean;
          allowSafari?: boolean;
          device: Id<"devices">;
          filterBrowsers?: boolean;
          filterSockets?: boolean;
        },
        null
      >;
      rebuild: FunctionReference<"mutation", "public", { device: Id<"devices"> }, null>;
      unscope: FunctionReference<"action", "public", { device: Id<"devices"> }, null>;
      scope: FunctionReference<"action", "public", { device: Id<"devices"> }, null>;
      apps: FunctionReference<
        "action",
        "public",
        { device: Id<"devices"> },
        Array<{ identifier: string; version: string }> | null
      >;
    };
  };
  groups: {
    create: {
      group: FunctionReference<
        "mutation",
        "public",
        {
          name: string;
          type: "organization" | "family" | "chaver";
          user: Id<"users">;
        },
        Id<"groups">
      >;
    };
    list: {
      all: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            group_id: Id<"groups">;
            name: string;
            owner: Id<"users">;
            type: "organization" | "family" | "chaver";
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
    };
  };
  jamf: {
    commands: {
      send: FunctionReference<
        "action",
        "public",
        {
          command:
            | "BlankPush"
            | "ClearPasscode"
            | "ClearRestrictionsPassword"
            | "RestartDevice"
            | "SettingsEnablePersonalHotspot"
            | "SettingsDisablePersonalHotspot"
            | "UpdateInventory"
            | "InstalledApplicationList";
          device: Id<"devices">;
        },
        any
      >;
    };
    devices: {
      apps: FunctionReference<
        "action",
        "public",
        { device: Id<"devices"> },
        Array<{ allowed: boolean; bundleId: string }>
      >;
    };
  };
  levels: {
    apps: {
      update: FunctionReference<
        "mutation",
        "public",
        {
          allowedApps?: Array<Id<"apps">>;
          appFilter?: Record<
            string,
            | true
            | {
                exclude?: { tags: Array<string>; type: "any" | "all" };
                include?: { tags: Array<string>; type: "any" | "all" };
              }
          >;
          appMode?: "whitelist" | "blacklist" | "categories";
          blockedApps?: Array<Id<"apps">>;
          level: Id<"levels">;
        },
        any
      >;
    };
    create: {
      level: FunctionReference<
        "mutation",
        "public",
        {
          description: string;
          group?: Id<"groups">;
          name: string;
          template: boolean;
        },
        Id<"levels">
      >;
    };
    delete: {
      level: FunctionReference<"mutation", "public", { level: Id<"levels"> }, any>;
    };
    list: {
      all: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"levels">;
            allowedApps: Array<Id<"apps">>;
            allowedUrls?: Array<string>;
            appFilter: Record<
              string,
              | true
              | {
                  exclude?: { tags: Array<string>; type: "any" | "all" };
                  include?: { tags: Array<string>; type: "any" | "all" };
                }
            >;
            appMode?: "whitelist" | "blacklist" | "categories";
            blockedApps: Array<Id<"apps">>;
            blockedUrls?: Array<string>;
            blueJayRules?: Record<string, any> | false;
            browserMode?: "none" | "categories" | "whitelist";
            description?: string;
            group?: Id<"groups">;
            name: string;
            policyCategories: Array<string>;
            private: boolean;
            restrictions: Array<string>;
            template: boolean;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      withApp: FunctionReference<
        "query",
        "public",
        {
          app: Id<"apps">;
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            allowedApps: Array<Id<"apps">>;
            allowedUrls?: Array<string>;
            appFilter: Record<
              string,
              | true
              | {
                  exclude?: { tags: Array<string>; type: "any" | "all" };
                  include?: { tags: Array<string>; type: "any" | "all" };
                }
            >;
            appMode?: "whitelist" | "blacklist" | "categories";
            blockedApps: Array<Id<"apps">>;
            blockedUrls?: Array<string>;
            blueJayRules?: Record<string, any> | false;
            browserMode?: "none" | "categories" | "whitelist";
            description?: string;
            group?: Id<"groups">;
            name: string;
            policyCategories: Array<string>;
            private: boolean;
            restrictions: Array<string>;
            template: boolean;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      withRule: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          rule: Id<"rules">;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            allowedApps: Array<Id<"apps">>;
            allowedUrls?: Array<string>;
            appFilter: Record<
              string,
              | true
              | {
                  exclude?: { tags: Array<string>; type: "any" | "all" };
                  include?: { tags: Array<string>; type: "any" | "all" };
                }
            >;
            appMode?: "whitelist" | "blacklist" | "categories";
            blockedApps: Array<Id<"apps">>;
            blockedUrls?: Array<string>;
            blueJayRules?: Record<string, any> | false;
            browserMode?: "none" | "categories" | "whitelist";
            description?: string;
            group?: Id<"groups">;
            name: string;
            policyCategories: Array<string>;
            private: boolean;
            restrictions: Array<string>;
            template: boolean;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      available: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"levels">;
            allowedApps: Array<Id<"apps">>;
            allowedUrls?: Array<string>;
            appFilter: Record<
              string,
              | true
              | {
                  exclude?: { tags: Array<string>; type: "any" | "all" };
                  include?: { tags: Array<string>; type: "any" | "all" };
                }
            >;
            appMode?: "whitelist" | "blacklist" | "categories";
            blockedApps: Array<Id<"apps">>;
            blockedUrls?: Array<string>;
            blueJayRules?: Record<string, any> | false;
            browserMode?: "none" | "categories" | "whitelist";
            description?: string;
            group?: Id<"groups">;
            name: string;
            policyCategories: Array<string>;
            private: boolean;
            restrictions: Array<string>;
            template: boolean;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
    };
    policies: {
      update: FunctionReference<
        "mutation",
        "public",
        {
          allowlist?: Array<string>;
          blocklist?: Array<string>;
          blueJayRules?: Record<string, any> | false;
          browserMode?: "none" | "categories" | "whitelist";
          level: Id<"levels">;
          policyCategories?: Array<string>;
        },
        any
      >;
    };
    restrictions: {
      update: FunctionReference<"mutation", "public", { level: Id<"levels">; restrictions: Array<string> }, any>;
    };
    rules: {
      update: FunctionReference<
        "mutation",
        "public",
        {
          add?: Array<Id<"rules">>;
          level: Id<"levels">;
          remove?: Array<Id<"rules">>;
        },
        any
      >;
    };
    visibility: {
      update: FunctionReference<"mutation", "public", { level: Id<"levels">; private: boolean }, any>;
    };
  };
  requests: {
    ai: {
      appRequest: FunctionReference<
        "mutation",
        "public",
        { aiNotes: string; aiSuggestion: string; request: Id<"appRequests"> },
        any
      >;
      webRequest: FunctionReference<
        "mutation",
        "public",
        { aiNotes: string; aiSuggestion: string; request: Id<"webRequests"> },
        any
      >;
    };
    create: {
      appRequest: FunctionReference<
        "mutation",
        "public",
        {
          app: string;
          bundleId: string;
          category?: string;
          iconUrl?: string;
          message?: string;
          name?: string;
          serial: string;
          tags?: Array<string>;
          trackId?: string;
          version?: string;
        },
        any
      >;
      webRequest: FunctionReference<
        "mutation",
        "public",
        { category?: string; message?: string; serial: string; url: string },
        any
      >;
    };
    list: {
      appRequestsByDevice: FunctionReference<
        "query",
        "public",
        {
          device: Id<"devices">;
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"appRequests">;
            aiNotes?: string;
            aiSuggestion?: string;
            app: string;
            bundleId: string;
            category?: string;
            device: Id<"devices">;
            iconUrl?: string;
            message?: string;
            name?: string;
            rejectedAt?: number;
            response?: "level" | "device" | "deny";
            responseTime?: number;
            tags?: Array<string>;
            trackId?: string;
            version?: string;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      myAppRequests: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          serial: string;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"appRequests">;
            aiNotes?: string;
            aiSuggestion?: string;
            app: string;
            bundleId: string;
            category?: string;
            device: Id<"devices">;
            iconUrl?: string;
            message?: string;
            name?: string;
            rejectedAt?: number;
            response?: "level" | "device" | "deny";
            responseTime?: number;
            tags?: Array<string>;
            trackId?: string;
            version?: string;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      webRequestsByDevice: FunctionReference<
        "query",
        "public",
        {
          device: Id<"devices">;
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"webRequests">;
            aiNotes?: string;
            aiSuggestion?: string;
            category?: string;
            device: Id<"devices">;
            message?: string;
            response?: "level" | "device" | "deny";
            responseTime?: number;
            url: string;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      myWebRequests: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          serial: string;
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"webRequests">;
            aiNotes?: string;
            aiSuggestion?: string;
            category?: string;
            device: Id<"devices">;
            message?: string;
            response?: "level" | "device" | "deny";
            responseTime?: number;
            url: string;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
    };
    respond: {
      approveForDevice: FunctionReference<
        "mutation",
        "public",
        {
          app: {
            category: string;
            iconUrl: string;
            name: string;
            tags: Array<string>;
            trackId: string;
            version?: string;
          };
          request: Id<"appRequests">;
        },
        any
      >;
      rejectForDevice: FunctionReference<"mutation", "public", { request: Id<"appRequests"> }, any>;
      approveWebRequestForDevice: FunctionReference<"mutation", "public", { request: Id<"webRequests"> }, any>;
      rejectWebRequestForDevice: FunctionReference<"mutation", "public", { request: Id<"webRequests"> }, any>;
    };
  };
  rules: {
    add: {
      rule: FunctionReference<
        "mutation",
        "public",
        {
          bundleId: string;
          description: string;
          name: string;
          parent?: Id<"apps">;
          urls: Array<string>;
        },
        any
      >;
    };
    delete: {
      rule: FunctionReference<"action", "public", { id: Id<"rules"> }, any>;
    };
    get: {
      rules: FunctionReference<
        "query",
        "public",
        { rules: Array<Id<"rules">> },
        Array<{
          bundleId: string;
          description: string;
          lastCheck: number;
          name: string;
          parent?: Id<"apps">;
          urls: Array<string>;
        }>
      >;
    };
    list: {
      all: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"rules">;
            bundleId: string;
            description: string;
            lastCheck: number;
            name: string;
            parent?: Id<"apps">;
            urls: Array<string>;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      inLevel: FunctionReference<
        "query",
        "public",
        { level: Id<"levels"> },
        Array<{
          bundleId: string;
          description: string;
          lastCheck: number;
          name: string;
          parent?: Id<"apps">;
          urls: Array<string>;
        }>
      >;
      availableForApp: FunctionReference<
        "query",
        "public",
        { app: Id<"apps"> },
        Array<{
          bundleId: string;
          description: string;
          lastCheck: number;
          name: string;
          parent?: Id<"apps">;
          urls: Array<string>;
        }>
      >;
    };
    update: {
      rule: FunctionReference<
        "mutation",
        "public",
        {
          bundleId?: string;
          description?: string;
          name?: string;
          rule: Id<"rules">;
          urls?: Array<string>;
        },
        any
      >;
      levels: FunctionReference<
        "mutation",
        "public",
        {
          add?: Array<Id<"levels">>;
          remove?: Array<Id<"levels">>;
          rule: Id<"rules">;
        },
        any
      >;
      lastCheck: FunctionReference<"mutation", "public", { rule: Id<"rules"> }, any>;
    };
  };
  siteCategories: {
    add: {
      category: FunctionReference<"mutation", "public", { name: string }, any>;
    };
    delete: {
      category: FunctionReference<"mutation", "public", { category: Id<"siteCategories"> }, any>;
    };
    list: {
      categories: FunctionReference<"query", "public", Record<string, never>, Array<string>>;
    };
    update: {
      category: FunctionReference<"mutation", "public", { name: string }, any>;
    };
  };
  users: {
    create: {
      user: FunctionReference<
        "mutation",
        "public",
        {
          email?: string;
          firstName: string;
          identity?: "parent" | "child" | "mentor" | "mentee" | "self-managed" | "organization";
          lastName: string;
          phone?: string;
        },
        Id<"users">
      >;
      manager: FunctionReference<
        "action",
        "public",
        {
          email: string;
          firstName: string;
          identity?: "parent" | "child" | "mentor" | "mentee" | "self-managed" | "organization";
          lastName: string;
          password: string;
          phone?: string;
        },
        Id<"users">
      >;
    };
    get: {
      fromCode: FunctionReference<
        "query",
        "public",
        { code: string },
        null | {
          email?: string;
          firstName: string;
          identity?: "parent" | "child" | "mentor" | "mentee" | "self-managed" | "organization";
          lastName: string;
          phone?: string;
        }
      >;
      roles: FunctionReference<
        "query",
        "public",
        { user: Id<"users"> },
        Array<{ group: Id<"groups">; type: "admin" | "viewer"; user: string }>
      >;
      me: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          group: null | {
            id: Id<"groups">;
            name: string;
            type: "organization" | "family" | "chaver";
          };
          type: "admin" | "viewer";
        }>
      >;
      byClerkId: FunctionReference<"query", "public", { id: string }, null | Id<"users">>;
      byConvexId: FunctionReference<
        "query",
        "public",
        { userId: Id<"users"> },
        Array<{
          associatedUsers: Array<{
            email?: string;
            firstName: string;
            identity: string;
            lastName: string;
            userId: Id<"users">;
          }>;
          deviceId: Id<"devices">;
          deviceName: string;
        }>
      >;
    };
    list: {
      all: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"users">;
            clerkId?: string;
            email?: string;
            firstName: string;
            identity?: "parent" | "child" | "mentor" | "mentee" | "self-managed" | "organization";
            lastName: string;
            phone?: string;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
    };
    roles: {
      add: FunctionReference<
        "mutation",
        "public",
        { group: Id<"groups">; type: "admin" | "viewer"; user: Id<"users"> },
        any
      >;
      remove: FunctionReference<"mutation", "public", { role: Id<"userRoles"> }, any>;
    };
    invite: {
      acceptInvitation: FunctionReference<
        "mutation",
        "public",
        { invitationId: Id<"invitations"> },
        | { groupId: Id<"groups">; status: "success" }
        | {
            status: "expired" | "already_used" | "not_found" | "no_device" | "not_authorized";
          }
      >;
      getInvitationById: FunctionReference<
        "query",
        "public",
        { id: Id<"invitations"> },
        {
          device?: Id<"devices">;
          email: string;
          expiresAt: number;
          invitorEmail: string;
          invitorFirstName: string;
          invitorLastName: string;
          isExpired: boolean;
          isUsed: boolean;
          usedAt?: number;
        } | null
      >;
      getInvitationsByInvitorEmail: FunctionReference<
        "query",
        "public",
        { deviceCode: string; invitorEmail: string },
        Array<{
          device?: Id<"devices">;
          email: string;
          expiresAt: number;
          id: Id<"invitations">;
          invitorEmail: string;
          invitorFirstName: string;
          invitorLastName: string;
          isExpired: boolean;
          isUsed: boolean;
          usedAt?: number;
        }>
      >;
      updateInvitationEmail: FunctionReference<
        "mutation",
        "public",
        {
          deviceCode: string;
          invitationId: Id<"invitations">;
          invitorEmail: string;
          newEmail: string;
        },
        | { message: string; status: "success" }
        | {
            message: string;
            status: "not_found" | "expired" | "device_mismatch" | "invitor_mismatch" | "already_used";
          }
      >;
    };
    delete: {
      user: FunctionReference<"action", "public", { user: Id<"users"> }, any>;
    };
  };
  webkitRequests: {
    list: {
      all: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          serial?: string;
          status?: "pending" | "approved" | "denied";
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _creationTime: number;
            _id: Id<"webkitRequests">;
            app: string;
            serial: string;
            status: "pending" | "approved" | "denied";
            url: string;
          }>;
          pageStatus?: "SplitRecommended" | "SplitRequired" | null;
          splitCursor?: string | null;
        }
      >;
      get: FunctionReference<
        "query",
        "public",
        { id: Id<"webkitRequests"> },
        {
          _creationTime: number;
          _id: Id<"webkitRequests">;
          app: string;
          serial: string;
          status: "pending" | "approved" | "denied";
          url: string;
        } | null
      >;
    };
    update: {
      status: FunctionReference<
        "mutation",
        "public",
        { id: Id<"webkitRequests">; status: "approved" | "denied" },
        null
      >;
      bulkStatus: FunctionReference<
        "mutation",
        "public",
        { ids: Array<Id<"webkitRequests">>; status: "approved" | "denied" },
        { notFound: Array<Id<"webkitRequests">>; updated: number }
      >;
      remove: FunctionReference<"mutation", "public", { id: Id<"webkitRequests"> }, null>;
    };
  };
};
export type InternalApiType = {};
